import { useEffect, useState } from "react";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";

import CasaLogo from "../../logo/CasaLogo";
import { logout } from "../../../keycloak";
import { buildMailto } from "../../../utils/mailto";
import {
  getInternalTenantList,
  switchInternalTenant,
  Tenant,
} from "../../../features/TenantSelection/services/api";
import styles from "./AccessRestriction.module.scss";

export type AccessRestrictionReason =
  | "TENANT_INACTIVE"
  | "BUSINESS_INACTIVE"
  | "NO_TENANT_ACCESS"
  | "NO_SUBSCRIPTION";

interface AccessRestrictionProps {
  reason: AccessRestrictionReason;
  businessName?: string;
  /** TENANT_INACTIVE only — the deactivated tenant's name, shown for both a regular user and an
   * internal user (who additionally gets the TenantPicker below). */
  tenantName?: string;
  /** Whoever hit the restriction, for the prefilled support mailto — not shown in the UI. */
  userEmail?: string;
  /** Offers a tenant picker instead of a dead end — see TenantPicker below. Only meaningful for
   * TENANT_INACTIVE / NO_TENANT_ACCESS; a deactivated business (or one with no active
   * subscription) is not a deadlock — the navbar's business switcher already covers it. */
  isInternalUser?: boolean;
}

const COPY: Record<
  AccessRestrictionReason,
  { icon: JSX.Element; chip: string; title: string; body: (businessName?: string) => string }
> = {
  TENANT_INACTIVE: {
    icon: <BlockOutlinedIcon className={styles.icon} />,
    chip: "Inactive",
    title: "Account Deactivated",
    body: (tenantName) =>
      tenantName
        ? `${tenantName} has been deactivated. Contact us to restore access.`
        : "Your CASA account has been deactivated. Contact us to restore access.",
  },
  BUSINESS_INACTIVE: {
    icon: <StorefrontOutlinedIcon className={styles.icon} />,
    chip: "Business Inactive",
    title: "Business Deactivated",
    body: (businessName) =>
      `${businessName ?? "This business"} has been deactivated. Switch to another business from the selector above, or contact us.`,
  },
  NO_TENANT_ACCESS: {
    icon: <PersonSearchOutlinedIcon className={styles.icon} />,
    chip: "No Access",
    title: "No Tenant Access",
    body: () => "We couldn't find a tenant for this account. Contact us for access.",
  },
  NO_SUBSCRIPTION: {
    icon: <CardMembershipOutlinedIcon className={styles.icon} />,
    chip: "Not Subscribed",
    title: "No Active Subscription",
    body: (businessName) =>
      `${businessName ?? "This business"} has no active subscription. Switch to another business from the selector above, or contact us.`,
  },
};

// Prefilled so support has the basics (who, which tenant/business, roughly when) without a
// back-and-forth — the person still has to click send themselves, nothing fires automatically.
export const buildSupportMailto = (
  chip: string,
  userEmail?: string,
  subjectName?: string,
  subjectLabel?: "Tenant" | "Business",
): string =>
  buildMailto("care@casaretail.ai", `CASA access issue — ${chip}`, [
    `Reason: ${chip}`,
    userEmail && `User: ${userEmail}`,
    subjectName && subjectLabel && `${subjectLabel}: ${subjectName}`,
    `Attempted at: ${new Date().toString()}`,
  ]);

const TenantPicker = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getInternalTenantList({ tenant: search || undefined, limit: 10 });
        setTenants(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch tenants");
        setTenants([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSelect = async (tenant: Tenant) => {
    if (switching) return;
    setSwitching(true);
    setError(null);
    try {
      await switchInternalTenant(tenant.id);
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Tenant switch failed");
      setSwitching(false);
    }
  };

  return (
    <Box className={styles.picker}>
      <Typography variant="subtitle2" className={styles.pickerLabel}>
        Switch to another tenant
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Search by tenant name"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={switching}
      />
      {error && (
        <Alert severity="error" className={styles.pickerAlert}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Skeleton variant="rectangular" height={120} className={styles.pickerSkeleton} />
      ) : (
        <List className={styles.pickerList}>
          {tenants.length === 0 && (
            <Typography variant="body2" color="text.secondary" className={styles.pickerEmpty}>
              No tenants found
            </Typography>
          )}
          {tenants.map((tenant) => (
            <ListItemButton key={tenant.id} disabled={switching} onClick={() => handleSelect(tenant)}>
              <ListItemText
                primary={tenant.esKey}
                secondary={tenant.zohoCustomerName ? `#${tenant.zohoCustomerName}` : `ID: ${tenant.id}`}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

const AccessRestriction = ({
  reason,
  businessName,
  tenantName,
  userEmail,
  isInternalUser,
}: AccessRestrictionProps) => {
  const copy = COPY[reason];
  const isBusinessLevel = reason === "BUSINESS_INACTIVE" || reason === "NO_SUBSCRIPTION";
  const showPicker = isInternalUser && !isBusinessLevel;
  const subjectName = reason === "TENANT_INACTIVE" ? tenantName : businessName;
  const supportMailto = buildSupportMailto(
    copy.chip,
    userEmail,
    subjectName,
    reason === "TENANT_INACTIVE" ? "Tenant" : isBusinessLevel ? "Business" : undefined,
  );

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <CasaLogo />
      </Box>

      <Box className={styles.body}>
        <Box className={styles.card}>
          <Box className={styles.iconWrapper}>{copy.icon}</Box>

          <Chip
            label={copy.chip}
            size="small"
            className={styles.chip}
            sx={{ bgcolor: "error.light", color: "error.dark", fontWeight: 400 }}
          />

          <Typography variant="h5" className={styles.title}>
            {copy.title}
          </Typography>

          <Typography variant="body2" className={styles.subtitle}>
            {copy.body(subjectName)}
          </Typography>

          {showPicker && (
            <>
              <Divider className={styles.divider} />
              <TenantPicker />
            </>
          )}

          <Divider className={styles.divider} />

          <Box className={styles.supportBox}>
            <MailOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Need help? Reach out to our support team
              </Typography>
              <Typography
                component="a"
                href={supportMailto}
                variant="body2"
                fontWeight={600}
                color="text.primary"
                sx={{ textDecoration: "none" }}
              >
                care@casaretail.ai
              </Typography>
            </Box>
          </Box>

          {!isBusinessLevel && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<LogoutOutlinedIcon />}
              onClick={logout}
              className={styles.logoutButton}
            >
              Sign out
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AccessRestriction;
